<template>
  <WithAsideCards
    :data="data?.fields?.references?.filter?.(instanceOfAsideCard)"
    data-distributor-list-v="3.2.0"
  >
    <div :class="$style.contentContainer">
      <div :class="$style.dataControls">
        <ClientOnly>
          <BaseSelect
            v-if="showMobileSortBy"
            v-model="sort"
            :class="$style.sort"
            :placeholder="localizedStrings['sort-by']"
            :options="
              computedSortOptions
                .filter((option) => option.sortable && !option.disabled)
                .map((option) => ({
                  label: option.title,
                  value: `desc${option.value}`,
                }))
            "
            :active-filter-value="sort"
          />
        </ClientOnly>
        <div :class="$style.filters">
          <ClientOnly>
            <BaseSelect
              v-model="selectedNameFilter"
              :class="$style.sort"
              :placeholder="localizedStrings['name']"
              :options="computedConglomerateFilters"
            />
            <BaseSelect
              v-if="
                computedStateProvinceFilterOptions.length &&
                computedLocationIsUsOrCanada
              "
              v-model="selectedLocationFilter"
              :class="$style.sort"
              :placeholder="`${localizedStrings['state']}/${localizedStrings['province']}`"
              :options="computedStateProvinceFilterOptions"
            />
            <BaseSelect
              v-if="
                computedCountryFilterOptions && !computedLocationIsUsOrCanada
              "
              v-model="selectedLocationFilter"
              :class="$style.sort"
              :placeholder="`${localizedStrings['country']}/${localizedStrings['region']}`"
              :options="computedCountryFilterOptions"
            />
          </ClientOnly>
          <BaseButton :class="$style.clearFilters" @click="handleClearFilters">
            {{ localizedStrings['clear-filters'] }}
          </BaseButton>
        </div>
      </div>

      <div
        v-if="showDesktopSortBy"
        :class="[
          $style.sortControls,
          !computedLocationIsUsOrCanada && $style.sortControlsHideState,
        ]"
      >
        <template v-for="sortItem in computedSortOptions">
          <component
            :is="sortItem.sortable ? 'button' : 'div'"
            v-if="!sortItem.disabled"
            :key="sortItem.title"
            :class="$style.sortItem"
            @click="sortBy(sortItem.value)"
          >
            {{ sortItem.title }}
            <template v-if="sortItem.sortable">
              <IconChevronUp
                v-if="sort === `desc${sortItem.value}`"
                :class="$style.sortIcon"
              />
              <IconChevronDown v-else :class="$style.sortIcon" />
            </template>
          </component>
        </template>
      </div>

      <DataPeek
        v-if="data?.fields?.debug"
        :data="distributorGroupsData?.items?.[0]?.fields?.distributors"
        label="Distributor Groups"
      />
      <DataPeek
        v-if="data?.fields?.debug"
        :data="data"
        corner="tl"
        label="data"
      />
      <DataPeek
        v-if="data?.fields?.debug"
        :data="computedDistributors"
        corner="bl"
        label="Distributors"
      />

      <!-- Distributor Groups -->
      <div :class="$style.distributorGroups">
        <div v-if="!computedFilteredEntries.length" :class="$style.noResults">
          {{ localizedStrings['no-results'] || 'No Results' }}
        </div>
        <div v-if="computedFilteredEntries?.length" :class="$style.documents">
          <DistributorEntry
            v-for="distributor in computedFilteredEntries"
            :key="distributor?.sys?.id"
            :data="distributor"
            :sort="sort"
            :groups="distributorGroups"
            :sorted-entries="computedFilteredEntries"
            :location-is-us-or-can="computedLocationIsUsOrCanada"
            :location-filter="selectedLocationFilter"
          />
        </div>
      </div>
    </div>
  </WithAsideCards>
</template>

<script setup lang="ts">
import type {
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypeDistributorGroupSkeleton,
  TypeDistributorGroupWithoutUnresolvableLinksResponse,
  TypeDistributorWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

interface IDistributorWithConglomerate
  extends TypeDistributorWithoutUnresolvableLinksResponse {
  conglomerate: TypeDistributorGroupWithoutUnresolvableLinksResponse
}

const { locale } = useI18n()
const { $client, $visibility } = useNuxtApp()
const { localizedStrings } = useLocalizedStrings()
const siteMeasurementStore = useSiteMeasurementStore()

const cookieStore = useCookieStore()
const { data: distributorGroupsData } = await useAsyncData(
  'distributorGroups',
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeDistributorGroupSkeleton>(
        {
          content_type: 'distributorGroup',
          include: 10,
          locale: locale.value,
        },
      )
    return response
  },
  {
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)

defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

// Data
const selectedNameFilter = ref('')
const selectedLocationFilter = ref('')

const sort = ref('descname')

// Computed
const showMobileControls = computed(() => siteMeasurementStore?.vWidth < 1440)

const computedLocationIsUsOrCanada = computed(() => {
  const cookieStore = useCookieStore()
  return (
    cookieStore.countryCookie === 'country-united-states' ||
    cookieStore.countryCookie === 'country-canada'
  )
})

const computedSortOptions = computed(() => {
  const ls = localizedStrings.value
  return [
    {
      sortable: false,
      title: ls['logo'] || 'Logo',
      value: 'logo',
      hidden: true,
    },
    {
      sortable: true,
      title: ls['name'] || 'Name',
      value: 'name',
    },
    {
      sortable: false,
      title: ls['phone'] || 'Phone',
      value: 'phone',
    },
    {
      sortable: false,
      title: ls['city'] || 'City',
      value: 'city',
    },
    {
      sortable: true,
      title: `${ls['state'] || 'State'}/${ls['province'] || 'Province'}`,
      value: 'region',
      disabled: !computedLocationIsUsOrCanada.value,
    },
    {
      sortable: true,
      title: `${ls['country'] || 'Country'}/${ls['region'] || 'Region'}`,
      value: 'country',
    },
  ]
})

const distributorGroups = computed(() => {
  return distributorGroupsData?.value?.items || []
})

const computedDistributors = computed(() => {
  return distributorGroups?.value?.reduce?.<IDistributorWithConglomerate[]>(
    (accu, curr) => {
      const {
        fields: { distributors },
      } = curr
      if (distributors?.length) {
        distributors.forEach((distributor) => {
          const isVisible = $visibility(distributor?.fields?.visibility)
          if (distributor && isVisible) {
            accu.push({
              ...distributor,
              conglomerate: curr,
            })
          }
        })
      }
      return accu
    },
    [],
  )
})

const computedFilteredEntries = computed(() => {
  const filteredGroups = computedDistributors?.value?.filter?.(
    (distributor) => {
      const hasSelectedFilterName =
        selectedNameFilter.value !== ''
          ? distributor?.conglomerate?.fields?.conglomerateName ===
            selectedNameFilter.value
          : true
      const filterLocation = computedLocationIsUsOrCanada.value
        ? distributor?.fields?.state?.fields?.label
        : distributor?.fields?.country?.fields?.label
      const hasSelectedFilterLocation =
        selectedLocationFilter.value !== ''
          ? filterLocation === selectedLocationFilter.value
          : true
      if (distributor && hasSelectedFilterName && hasSelectedFilterLocation) {
        return distributor
      }
    },
  )

  // Sort - Name
  if (sort.value === 'descname') {
    return filteredGroups.sort((a, b) => {
      const companyNameA = a?.fields?.companyName || ''
      const companyNameB = b?.fields?.companyName || ''
      return companyNameA?.localeCompare?.(companyNameB, locale?.value)
    })
  }
  if (sort.value === 'ascname') {
    return filteredGroups.sort((a, b) => {
      const companyNameA = a?.fields?.companyName || ''
      const companyNameB = b?.fields?.companyName || ''
      return -1 * companyNameA?.localeCompare?.(companyNameB, locale?.value)
    })
  }

  // Sort - Region
  if (sort.value === 'descregion') {
    return filteredGroups.sort((a, b) => {
      const stateA = a?.fields?.state?.fields?.label || ''
      const stateB = b?.fields?.state?.fields?.label || ''
      return stateA?.localeCompare?.(stateB, locale?.value)
    })
  }
  if (sort.value === 'ascregion') {
    return filteredGroups.sort((a, b) => {
      const stateA = a?.fields?.state?.fields?.label || ''
      const stateB = b?.fields?.state?.fields?.label || ''
      return -1 * stateA?.localeCompare?.(stateB, locale?.value)
    })
  }

  // Sort - Country
  if (sort.value === 'desccountry') {
    return filteredGroups.sort((a, b) => {
      const countryA = a?.fields?.country?.fields?.label || ''
      const countryB = b?.fields?.country?.fields?.label || ''
      return countryA?.localeCompare?.(countryB, locale?.value)
    })
  }
  if (sort.value === 'asccountry') {
    return filteredGroups.sort((a, b) => {
      const countryA = a?.fields?.country?.fields?.label || ''
      const countryB = b?.fields?.country?.fields?.label || ''
      return -1 * countryA?.localeCompare?.(countryB, locale?.value)
    })
  }

  return filteredGroups
})

const showMobileSortBy = computed(
  () => computedFilteredEntries?.value?.length && showMobileControls.value,
)
const showDesktopSortBy = computed(
  () => computedFilteredEntries?.value?.length && !showMobileControls.value,
)

const computedConglomerateFilters = computed(() => {
  const uniqueConglomerateNames = new Set(
    computedDistributors?.value?.map?.(
      (entry) => entry?.conglomerate?.fields?.conglomerateName || '',
    ),
  )
  return [...uniqueConglomerateNames].sort((a = '', b = '') => (a > b ? 1 : -1))
})

const computedCountryFilterOptions = computed(() => {
  const uniqueCountries = new Set(
    computedDistributors?.value?.map?.(
      (distributor) => distributor?.fields?.country?.fields?.label || '',
    ),
  )
  return [...uniqueCountries].sort((a, b) => (a > b ? 1 : -1))
})

const computedStateProvinceFilterOptions = computed(() => {
  const uniqueStates = new Set(
    computedDistributors?.value?.map?.(
      (distributor) => distributor?.fields?.state?.fields?.label || '',
    ),
  )
  return [...uniqueStates].sort((a = '', b = '') => (a > b ? 1 : -1))
})

// Methods
const sortBy = (sortType: string) => {
  if (sortType === 'name') {
    sort.value = sort.value === 'descname' ? 'ascname' : 'descname'
  }

  if (sortType === 'region') {
    sort.value = sort.value === 'descregion' ? 'ascregion' : 'descregion'
  }

  if (sortType === 'country') {
    sort.value = sort.value === 'desccountry' ? 'asccountry' : 'desccountry'
  }
}

const handleClearFilters = () => {
  selectedNameFilter.value = ''
  selectedLocationFilter.value = ''
  sortBy(sort.value)
}
</script>

<style lang="scss" module>
.contentContainer {
  color: var(--gray-8);
  min-width: 0;
  text-align: center;

  @include media('xl') {
    text-align: left;
  }
}

.dataControls {
  margin-bottom: 10px;

  @include media('xl') {
    margin-bottom: 0;
  }
}

.filters {
  align-items: center;
  display: grid;
  grid-template-columns: 1fr;

  @include media('xl') {
    gap: 10px;
    grid-template-columns: 300px 290px 1fr;
  }
}

.clearFilters {
  @include media('xl') {
    margin-left: auto;
  }
}

.sort {
  margin-bottom: 10px;
  width: 100%;
}

.sortControls {
  align-items: center;
  background-color: var(--gray-8);
  color: var(--white);
  display: grid;
  font-size: to-rem(17px);
  line-height: math.div(27, 17);
  gap: 15px;
  grid-template-columns: 120px 1.25fr 1fr 1fr 1.1fr 1.2fr;
  padding: 9px 24px;

  &.sortControlsHideState {
    grid-template-columns: 120px 1fr 1fr 1fr 1fr;
  }
}

.sortItem {
  align-items: center;
  background-color: transparent;
  border: none;
  color: inherit;
  display: flex;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  justify-content: center;
  padding: 0;
}

.sortIcon {
  height: 6px;
  margin-left: 5px;
  width: 11px;
}

.distributorGroups {
  background-color: var(--white);
  box-shadow: 0 4px 13px rgba(#000, 0.06);
  padding: 24px;
}
</style>
